import React, { useState } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FactoryQuotaManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [showAddFactory, setShowAddFactory] = useState(false);
  const [showAddQuota, setShowAddQuota] = useState(false);
  const [selectedFactory, setSelectedFactory] = useState(null);
  const [showEditFactory, setShowEditFactory] = useState(false);
  const [showViewDetails, setShowViewDetails] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);

  // Mock data for factories
  const [factories, setFactories] = useState([
    {
      id: 1,
      name: 'Maharashtra Sugar Mills',
      location: 'Pune, Maharashtra',
      city: 'Pune',
      state: 'Maharashtra',
      contactPerson: 'Rajesh Kumar',
      contactPhone: '+91 98765 43210',
      contactEmail: 'rajesh@maharashtrasugar.com',
      grades: ['ICUMSA 45', 'VHP', 'Raw Sugar'],
      image: '/assets/images/no_image.png',
      status: 'active',
      addedDate: '2025-11-15'
    },
    {
      id: 2,
      name: 'Gujarat Sugar Industries',
      location: 'Ahmedabad, Gujarat',
      city: 'Ahmedabad',
      state: 'Gujarat',
      contactPerson: 'Amit Patel',
      contactPhone: '+91 98765 43211',
      contactEmail: 'amit@gujaratsugar.com',
      grades: ['ICUMSA 45', 'Refined Sugar'],
      image: '/assets/images/no_image.png',
      status: 'active',
      addedDate: '2025-11-10'
    }
  ]);

  // Mock data for quotas
  const [quotas, setQuotas] = useState([
    {
      id: 1,
      factoryId: 1,
      factoryName: 'Maharashtra Sugar Mills',
      quantity: 500,
      pricePerTon: 45000,
      grade: 'ICUMSA 45',
      gradeDetails: 'Premium white refined sugar',
      status: 'available',
      availableFrom: null,
      validUntil: '2025-12-31',
      updatedDate: '2025-12-01'
    },
    {
      id: 2,
      factoryId: 1,
      factoryName: 'Maharashtra Sugar Mills',
      quantity: 300,
      pricePerTon: 42000,
      grade: 'VHP',
      gradeDetails: 'Very High Polarization raw sugar',
      status: 'available_from',
      availableFrom: '2025-12-15',
      validUntil: '2026-01-15',
      updatedDate: '2025-12-01'
    },
    {
      id: 3,
      factoryId: 2,
      factoryName: 'Gujarat Sugar Industries',
      quantity: 400,
      pricePerTon: 46000,
      grade: 'ICUMSA 45',
      gradeDetails: 'Premium white refined sugar',
      status: 'not_available',
      availableFrom: null,
      validUntil: null,
      updatedDate: '2025-11-28'
    }
  ]);

  const [factoryFormData, setFactoryFormData] = useState({
    name: '',
    city: '',
    state: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    grades: [],
    image: null,
    imagePreview: null
  });

  const [quotaFormData, setQuotaFormData] = useState({
    factoryId: '',
    quantity: '',
    pricePerTon: '',
    grade: '',
    gradeDetails: '',
    status: 'available',
    availableFrom: '',
    validUntil: ''
  });

  const tabs = [
    { id: 'analytics', label: 'Analytics Overview', icon: 'BarChart3' },
    { id: 'factories', label: 'Factory Management', icon: 'Factory' }
  ];

  const availableGrades = ['ICUMSA 45', 'VHP', 'Raw Sugar', 'S1', 'S2', 'ICUMSA 100', 'Pharma Grade Sugar', 'S30', 'M30'];

  const handleImageUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFactoryFormData({
          ...factoryFormData,
          image: file,
          imagePreview: reader?.result
        });
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleAddFactory = () => {
    if (!factoryFormData?.name || !factoryFormData?.city || !factoryFormData?.state) {
      alert('Please fill all required fields');
      return;
    }

    const newFactory = {
      id: factories?.length + 1,
      name: factoryFormData?.name,
      location: `${factoryFormData?.city}, ${factoryFormData?.state}`,
      city: factoryFormData?.city,
      state: factoryFormData?.state,
      contactPerson: factoryFormData?.contactPerson,
      contactPhone: factoryFormData?.contactPhone,
      contactEmail: factoryFormData?.contactEmail,
      grades: factoryFormData?.grades,
      image: factoryFormData?.imagePreview || '/assets/images/no_image.png',
      status: 'active',
      addedDate: new Date()?.toISOString()?.split('T')?.[0]
    };

    setFactories([...factories, newFactory]);
    setShowAddFactory(false);
    setFactoryFormData({
      name: '',
      city: '',
      state: '',
      contactPerson: '',
      contactPhone: '',
      contactEmail: '',
      grades: [],
      image: null,
      imagePreview: null
    });
  };

  const handleAddQuota = () => {
    if (!quotaFormData?.factoryId || !quotaFormData?.quantity || !quotaFormData?.pricePerTon || !quotaFormData?.grade) {
      alert('Please fill all required fields');
      return;
    }

    const factory = factories?.find(f => f?.id === parseInt(quotaFormData?.factoryId));
    const newQuota = {
      id: quotas?.length + 1,
      factoryId: parseInt(quotaFormData?.factoryId),
      factoryName: factory?.name,
      quantity: parseFloat(quotaFormData?.quantity),
      pricePerTon: parseFloat(quotaFormData?.pricePerTon),
      grade: quotaFormData?.grade,
      gradeDetails: quotaFormData?.gradeDetails,
      status: quotaFormData?.status,
      availableFrom: quotaFormData?.availableFrom || null,
      validUntil: quotaFormData?.validUntil || null,
      updatedDate: new Date()?.toISOString()?.split('T')?.[0]
    };

    setQuotas([...quotas, newQuota]);
    setShowAddQuota(false);
    setQuotaFormData({
      factoryId: '',
      quantity: '',
      pricePerTon: '',
      grade: '',
      gradeDetails: '',
      status: 'available',
      availableFrom: '',
      validUntil: ''
    });
  };

  const handleEditFactory = (factory) => {
    setEditingFactory(factory);
    setFactoryFormData({
      name: factory?.name,
      city: factory?.city,
      state: factory?.state,
      contactPerson: factory?.contactPerson,
      contactPhone: factory?.contactPhone,
      contactEmail: factory?.contactEmail,
      grades: factory?.grades || [],
      image: null,
      imagePreview: factory?.image
    });
    setShowEditFactory(true);
  };

  const handleUpdateFactory = () => {
    if (!factoryFormData?.name || !factoryFormData?.city || !factoryFormData?.state) {
      alert('Please fill all required fields');
      return;
    }

    const updatedFactories = factories?.map(f => 
      f?.id === editingFactory?.id 
        ? {
            ...f,
            name: factoryFormData?.name,
            location: `${factoryFormData?.city}, ${factoryFormData?.state}`,
            city: factoryFormData?.city,
            state: factoryFormData?.state,
            contactPerson: factoryFormData?.contactPerson,
            contactPhone: factoryFormData?.contactPhone,
            contactEmail: factoryFormData?.contactEmail,
            grades: factoryFormData?.grades,
            image: factoryFormData?.imagePreview || f?.image
          }
        : f
    );

    setFactories(updatedFactories);
    setShowEditFactory(false);
    setEditingFactory(null);
    setFactoryFormData({
      name: '',
      city: '',
      state: '',
      contactPerson: '',
      contactPhone: '',
      contactEmail: '',
      grades: [],
      image: null,
      imagePreview: null
    });
  };

  const handleViewDetails = (factory) => {
    setSelectedFactory(factory);
    setShowViewDetails(true);
  };

  const handleDeleteFactory = (factoryId) => {
    if (window.confirm('Are you sure you want to delete this factory? This action cannot be undone.')) {
      setFactories(factories?.filter(f => f?.id !== factoryId));
      // Also delete associated quotas
      setQuotas(quotas?.filter(q => q?.factoryId !== factoryId));
    }
  };

  const updateQuotaStatus = (quotaId, newStatus, availableFrom = null, validUntil = null) => {
    setQuotas(quotas?.map(q => 
      q?.id === quotaId 
        ? { ...q, status: newStatus, availableFrom, validUntil, updatedDate: new Date()?.toISOString()?.split('T')?.[0] }
        : q
    ));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { label: 'Available', color: 'bg-green-100 text-green-800' },
      not_available: { label: 'Not Available', color: 'bg-red-100 text-red-800' },
      available_from: { label: 'Available From Date', color: 'bg-blue-100 text-blue-800' }
    };
    const config = statusConfig?.[status] || statusConfig?.available;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const renderFactoryManagement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-foreground">Registered Factories</h2>
        <Button
          onClick={() => setShowAddFactory(true)}
          className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
        >
          <Icon name="Plus" size={20} className="mr-2" />
          Add New Factory
        </Button>
      </div>

      {showAddFactory && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
          <h3 className="text-xl font-bold text-foreground mb-4">Add New Factory</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">Factory Photo</label>
              <div className="flex items-center gap-4">
                <div className="w-32 h-32 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-muted overflow-hidden">
                  {factoryFormData?.imagePreview ? (
                    <img 
                      src={factoryFormData?.imagePreview} 
                      alt="Factory preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      <Icon name="Image" size={32} className="mx-auto text-muted-foreground" />
                      <p className="text-xs text-muted-foreground mt-1">No photo</p>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="factory-image-upload"
                  />
                  <label
                    htmlFor="factory-image-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="Upload" size={16} />
                    Upload Photo
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">
                    Recommended: JPG, PNG. Max 5MB
                  </p>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Factory Name *</label>
              <input
                type="text"
                value={factoryFormData?.name}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, name: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter factory name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">City *</label>
              <input
                type="text"
                value={factoryFormData?.city}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, city: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">State *</label>
              <input
                type="text"
                value={factoryFormData?.state}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, state: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter state"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contact Person</label>
              <input
                type="text"
                value={factoryFormData?.contactPerson}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, contactPerson: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter contact person name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contact Phone</label>
              <input
                type="tel"
                value={factoryFormData?.contactPhone}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, contactPhone: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="+91 XXXXX XXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contact Email</label>
              <input
                type="email"
                value={factoryFormData?.contactEmail}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, contactEmail: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="contact@factory.com"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">Available Sugar Grades</label>
              <div className="flex flex-wrap gap-2">
                {availableGrades?.map(grade => (
                  <label key={grade} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={factoryFormData?.grades?.includes(grade)}
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          setFactoryFormData({ ...factoryFormData, grades: [...factoryFormData?.grades, grade] });
                        } else {
                          setFactoryFormData({ ...factoryFormData, grades: factoryFormData?.grades?.filter(g => g !== grade) });
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">{grade}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button onClick={handleAddFactory} className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white">
              Add Factory
            </Button>
            <Button onClick={() => setShowAddFactory(false)} variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {showEditFactory && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
          <h3 className="text-xl font-bold text-foreground mb-4">Edit Factory Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">Factory Photo</label>
              <div className="flex items-center gap-4">
                <div className="w-32 h-32 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-muted overflow-hidden">
                  {factoryFormData?.imagePreview ? (
                    <img 
                      src={factoryFormData?.imagePreview} 
                      alt="Factory preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      <Icon name="Image" size={32} className="mx-auto text-muted-foreground" />
                      <p className="text-xs text-muted-foreground mt-1">No photo</p>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="factory-image-upload-edit"
                  />
                  <label
                    htmlFor="factory-image-upload-edit"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="Upload" size={16} />
                    Change Photo
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">
                    Recommended: JPG, PNG. Max 5MB
                  </p>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Factory Name *</label>
              <input
                type="text"
                value={factoryFormData?.name}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, name: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter factory name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">City *</label>
              <input
                type="text"
                value={factoryFormData?.city}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, city: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">State *</label>
              <input
                type="text"
                value={factoryFormData?.state}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, state: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter state"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contact Person</label>
              <input
                type="text"
                value={factoryFormData?.contactPerson}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, contactPerson: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter contact person name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contact Phone</label>
              <input
                type="tel"
                value={factoryFormData?.contactPhone}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, contactPhone: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="+91 XXXXX XXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contact Email</label>
              <input
                type="email"
                value={factoryFormData?.contactEmail}
                onChange={(e) => setFactoryFormData({ ...factoryFormData, contactEmail: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="contact@factory.com"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">Available Sugar Grades</label>
              <div className="flex flex-wrap gap-2">
                {availableGrades?.map(grade => (
                  <label key={grade} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={factoryFormData?.grades?.includes(grade)}
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          setFactoryFormData({ ...factoryFormData, grades: [...factoryFormData?.grades, grade] });
                        } else {
                          setFactoryFormData({ ...factoryFormData, grades: factoryFormData?.grades?.filter(g => g !== grade) });
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">{grade}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button onClick={handleUpdateFactory} className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white">
              Update Factory
            </Button>
            <Button onClick={() => {
              setShowEditFactory(false);
              setEditingFactory(null);
              setFactoryFormData({
                name: '',
                city: '',
                state: '',
                contactPerson: '',
                contactPhone: '',
                contactEmail: '',
                grades: [],
                image: null,
                imagePreview: null
              });
            }} variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {showViewDetails && selectedFactory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-foreground">Factory Details</h3>
              <button
                onClick={() => {
                  setShowViewDetails(false);
                  setSelectedFactory(null);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex items-start gap-6">
                <img 
                  src={selectedFactory?.image} 
                  alt={`${selectedFactory?.name} factory view`}
                  className="w-48 h-48 rounded-lg object-cover border-2 border-border"
                />
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-foreground mb-2">{selectedFactory?.name}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="MapPin" size={18} />
                      <span>{selectedFactory?.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        selectedFactory?.status === 'active' ?'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                      }`}>
                        {selectedFactory?.status?.toUpperCase()}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Added on {selectedFactory?.addedDate}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-border rounded-lg p-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Contact Person</p>
                  <p className="text-foreground font-medium">{selectedFactory?.contactPerson || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Phone</p>
                  <p className="text-foreground font-medium flex items-center gap-2">
                    <Icon name="Phone" size={14} />
                    {selectedFactory?.contactPhone || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Email</p>
                  <p className="text-foreground font-medium flex items-center gap-2">
                    <Icon name="Mail" size={14} />
                    {selectedFactory?.contactEmail || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">City</p>
                  <p className="text-foreground font-medium">{selectedFactory?.city}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">State</p>
                  <p className="text-foreground font-medium">{selectedFactory?.state}</p>
                </div>
              </div>

              <div className="border border-border rounded-lg p-4">
                <p className="text-sm text-muted-foreground uppercase font-medium mb-3">Available Sugar Grades</p>
                <div className="flex flex-wrap gap-2">
                  {selectedFactory?.grades?.map(grade => (
                    <span key={grade} className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                      {grade}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border border-border rounded-lg p-4">
                <p className="text-sm text-muted-foreground uppercase font-medium mb-3">Active Quotas</p>
                <div className="space-y-2">
                  {quotas?.filter(q => q?.factoryId === selectedFactory?.id)?.length > 0 ? (
                    quotas?.filter(q => q?.factoryId === selectedFactory?.id)?.map(quota => (
                      <div key={quota?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div>
                          <p className="text-sm font-medium text-foreground">{quota?.grade}</p>
                          <p className="text-xs text-muted-foreground">{quota?.quantity} tons @ ₹{quota?.pricePerTon?.toLocaleString()}/ton</p>
                        </div>
                        {getStatusBadge(quota?.status)}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No active quotas</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => {
                    setShowViewDetails(false);
                    handleEditFactory(selectedFactory);
                  }}
                  className="flex-1 bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
                >
                  <Icon name="Edit" size={16} className="mr-2" />
                  Edit Factory
                </Button>
                <Button 
                  onClick={() => {
                    setShowViewDetails(false);
                    setSelectedFactory(null);
                    handleDeleteFactory(selectedFactory?.id);
                  }}
                  variant="outline"
                  className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                >
                  <Icon name="Trash2" size={16} className="mr-2" />
                  Delete Factory
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {factories?.map(factory => (
          <div key={factory?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
            <div className="flex gap-4">
              <img 
                src={factory?.image} 
                alt={`${factory?.name} factory exterior view`}
                className="w-24 h-24 rounded-lg object-cover"
              />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground">{factory?.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                  <Icon name="MapPin" size={14} />
                  {factory?.location}
                </p>
                <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                  <Icon name="Phone" size={14} />
                  {factory?.contactPhone}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {factory?.grades?.map(grade => (
                    <span key={grade} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {grade}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1"
                onClick={() => handleEditFactory(factory)}
              >
                <Icon name="Edit" size={16} className="mr-2" />
                Edit
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1"
                onClick={() => handleViewDetails(factory)}
              >
                <Icon name="Eye" size={16} className="mr-2" />
                View Details
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-foreground">Analytics Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Factories</p>
              <p className="text-3xl font-bold mt-2">{factories?.length}</p>
            </div>
            <Icon name="Factory" size={40} className="opacity-80" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Active Quotas</p>
              <p className="text-3xl font-bold mt-2">{quotas?.filter(q => q?.status === 'available')?.length}</p>
            </div>
            <Icon name="Package" size={40} className="opacity-80" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Quantity</p>
              <p className="text-3xl font-bold mt-2">{quotas?.reduce((sum, q) => sum + q?.quantity, 0)} tons</p>
            </div>
            <Icon name="TrendingUp" size={40} className="opacity-80" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Avg Price/Ton</p>
              <p className="text-3xl font-bold mt-2">
                ₹{Math.round(quotas?.reduce((sum, q) => sum + q?.pricePerTon, 0) / quotas?.length)?.toLocaleString()}
              </p>
            </div>
            <Icon name="DollarSign" size={40} className="opacity-80" />
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {quotas?.slice(0, 5)?.map(quota => (
            <div key={quota?.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center">
                  <Icon name="Package" size={20} color="#FFFFFF" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{quota?.factoryName}</p>
                  <p className="text-xs text-muted-foreground">{quota?.grade} - {quota?.quantity} tons</p>
                </div>
              </div>
              <div className="text-right">
                {getStatusBadge(quota?.status)}
                <p className="text-xs text-muted-foreground mt-1">Updated: {quota?.updatedDate}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'factories':
        return renderFactoryManagement();
      case 'analytics':
        return renderAnalytics();
      default:
        return renderAnalytics();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center">
                <Icon name="Factory" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Quota Manager</h1>
                <p className="text-muted-foreground">Manage factories, quotas, and availability status</p>
              </div>
            </div>
          </div>

          {/* Desktop Tabs */}
          <div className="hidden md:block bg-card rounded-lg border border-border mb-6">
            <div className="flex items-center gap-2 p-2">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === tab?.id
                      ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white shadow-lg'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Tabs */}
          <div className="md:hidden mb-6">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e?.target?.value)}
              className="w-full px-4 py-3 bg-card border-2 border-[#ff8c00]/30 rounded-lg text-foreground font-medium"
            >
              {tabs?.map(tab => (
                <option key={tab?.id} value={tab?.id}>{tab?.label}</option>
              ))}
            </select>
          </div>

          <div className="transition-all duration-300">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FactoryQuotaManagerDashboard;